import { useAuthenticatedInfiniteQuery } from 'flow/hooks/api/useAuthenticatedQuery';
import AiCoworkerResource from 'flow/api/aicoworker';
import { ExtensionKey } from 'flow/types/EntityKeys';

export interface AICoworkerCallDetailEvals {
    locutusId: string;
    parentLocutusSessionId?: string;
    accountId: number;
    durationSeconds: number;
    recordingTimestamp: number;
    resolved?: boolean;
    satisfied?: boolean;
}

interface AICoworkerCallDetailEvalsResponse {
    calls: Array<AICoworkerCallDetailEvals>;
    nextCursor?: string;
}

export const useAiCoworkerCallDetailEvals = (
    extensionKey: ExtensionKey,
    limit: number = 30,
    enabled: boolean = true,
) => {
    const {
        data,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
    } = useAuthenticatedInfiniteQuery<AICoworkerCallDetailEvalsResponse>({
        queryKey: ['aiCoworkerCallDetailEvals', extensionKey, limit],
        queryFn: ({ pageParam }) =>
            new Promise<AICoworkerCallDetailEvalsResponse>(
                (resolve, reject) => {
                    AiCoworkerResource.getAICoworkerEvalDetailsStats(
                        {
                            onResult: (json: any) => {
                                // Handle the response structure with 'data' property
                                if (
                                    json &&
                                    typeof json === 'object' &&
                                    'data' in json
                                ) {
                                    resolve({
                                        calls: json.data || [],
                                        nextCursor: json.nextCursor?.toString(),
                                    });
                                } else if (Array.isArray(json)) {
                                    resolve({
                                        calls: json,
                                    });
                                } else {
                                    resolve({
                                        calls: [],
                                    });
                                }
                            },
                            onError: reject,
                            onClientError: reject,
                        },
                        extensionKey,
                        pageParam as string | undefined,
                        limit,
                    );
                },
            ),
        getNextPageParam: (lastPage) => lastPage.nextCursor,
        initialPageParam: undefined as string | undefined,
        enabled: !!extensionKey && enabled,
    });

    // Flatten all pages into a single array of evaluations
    const allEvaluations = data?.pages.flatMap((page) => page.calls) || [];

    return {
        data,
        allEvaluations,
        isLoading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
    };
};
